// Helpers puros del módulo Chat (sin dependencia del state local del componente).

import * as crypto from '../../crypto';
import * as storage from '../../storage';
import { t } from '../../i18n';
import type { ChatMessage, ChatState, GroupState } from './types';

/**
 * Fuente única: el estado de leído/no-leído vive SOLO en cada mensaje (`m.readAt`).
 * El contador de unread se DERIVA y nunca se mantiene como campo paralelo.
 */
export function unreadOfChat(c: ChatState): number {
  let n = 0;
  for (const m of c.messages) if (!m.fromMe && !m.readAt) n++;
  return n;
}

export function unreadOfGroup(g: GroupState): number {
  let n = 0;
  for (const m of g.messages) if (!m.fromMe && !m.readAt) n++;
  return n;
}

export function humanizeError(err: Error): string {
  const m = err.message;
  if (m.includes('HTTP 404')) return t('error.notFound');
  if (m.includes('HTTP 401')) return t('error.expired');
  if (m.includes('HTTP 410')) return t('error.gone');
  if (m.includes('HTTP 400')) return t('error.invalid');
  if (m.includes('HTTP 5')) return t('error.server');
  if (m.toLowerCase().includes('failed to fetch')) return t('error.network');
  return m;
}

export function kindFromContentType(ct: string): 'image' | 'video' | 'audio' | 'file' {
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  if (ct.startsWith('audio/')) return 'audio';
  return 'file';
}

export function uuid(): string {
  return globalThis.crypto.randomUUID();
}

export function serializeMessage(m: ChatMessage): storage.SerializedMessage {
  const base: any = {
    id: m.id,
    fromMe: m.fromMe,
    at: m.at,
    deliveredAt: m.deliveredAt ?? null,
    readAt: m.readAt ?? null,
    fromSyphraxId: m.fromSyphraxId,
    replyTo: m.replyTo ?? null,
    editedAt: m.editedAt ?? null,
    reactions: m.reactions ?? null,
  };
  if (m.kind === 'text') return { ...base, kind: 'text', text: m.text };
  if (m.kind === 'contact') return { ...base, kind: 'contact', contactSyphraxId: m.contactSyphraxId };
  return {
    ...base,
    kind: m.kind,
    mediaId: m.mediaId,
    fileKey: crypto.b64encode(m.fileKey),
    name: m.name,
    size: m.size,
    contentType: m.contentType,
  };
}

export function deserializeMessage(m: storage.SerializedMessage): ChatMessage {
  const ms = m as any;
  const base: any = {
    id: m.id,
    fromMe: m.fromMe,
    at: m.at,
    deliveredAt: m.deliveredAt ?? null,
    readAt: m.readAt ?? null,
    fromSyphraxId: m.fromSyphraxId,
    replyTo: ms.replyTo ?? undefined,
    editedAt: ms.editedAt ?? null,
    reactions: ms.reactions ?? undefined,
  };
  if (m.kind === 'text') return { ...base, kind: 'text', text: m.text };
  if (m.kind === 'contact') return { ...base, kind: 'contact', contactSyphraxId: m.contactSyphraxId };
  return {
    ...base,
    kind: m.kind,
    mediaId: m.mediaId,
    fileKey: crypto.b64decode(m.fileKey),
    name: m.name,
    size: m.size,
    contentType: m.contentType,
  };
}
